import React, { useMemo, useState } from "react";
import {
  Box,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import useGetCampaignSummaryReport from "./hooks/useGetCampaignSummaryReport";

export default function CampaignSummaryReport({ organizationId, campaignId }) {
  const { data: report, loading, notFound, error } = useGetCampaignSummaryReport({
    organizationId,
    campaignId,
  });

  const customerSelectionStrategySuggestions = useMemo(
    () => report?.customerSelectionStrategySuggestions ?? [],
    [report]
  );
  const productRecommendationStrategySuggestions = useMemo(
    () => report?.productRecommendationStrategySuggestions ?? [],
    [report]
  );

  const [expandedRows, setExpandedRows] = useState({});

  const showNoSuggestionsIndicator =
    notFound ||
    (!loading &&
      !error &&
      report &&
      customerSelectionStrategySuggestions.length === 0 &&
      productRecommendationStrategySuggestions.length === 0);

  const toggleRow = (key) => {
    setExpandedRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <Box mt={{ xs: "14px", sm: "20px" }}>
      <Stack
        sx={styles.root}
        direction={"column"}
        divider={<Divider orientation={"horizontal"} flexItem />}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          sx={styles.header}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <Box component={"h2"} sx={styles.headerTitle}>
              Following Campaigns Strategy Suggestions
            </Box>
          </Box>
        </Stack>

        <Box sx={styles.body}>
          {loading && <Box sx={styles.message}>Loading…</Box>}

          {!loading && showNoSuggestionsIndicator && (
            <Box sx={styles.message}>No suggestions available yet</Box>
          )}

          {!loading && !showNoSuggestionsIndicator && error && (
            <Box sx={styles.message}>Failed to load suggestions</Box>
          )}

          {!loading && !showNoSuggestionsIndicator && !error && (
            <Stack spacing={3}>
              <Box>
                <Box component={"h3"} sx={styles.tableTitle}>
                  Customer Selection Strategy
                </Box>
                <TableContainer component={Paper} elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...styles.th, ...styles.summaryCol }}>
                          Summary
                        </TableCell>
                        <TableCell sx={{ ...styles.th, ...styles.descriptionCol }}>
                          Description
                        </TableCell>
                        <TableCell
                          sx={{ ...styles.th, ...styles.countCol }}
                          align="right"
                        >
                          Count
                        </TableCell>
                        <TableCell sx={{ ...styles.th, ...styles.expandCol }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {customerSelectionStrategySuggestions.map((row, idx) => {
                        const key = `${row?.name ?? "row"}-${idx}`;
                        const queryText = row?.extraFields?.query;
                        const hasQuery = !!queryText;
                        const isExpanded = !!expandedRows[key];

                        return (
                          <React.Fragment key={key}>
                            <TableRow>
                              <TableCell
                                sx={{ ...styles.td, ...styles.summaryCol }}
                              >
                                {row?.name ?? "-"}
                              </TableCell>
                              <TableCell
                                sx={{ ...styles.td, ...styles.descriptionCol }}
                              >
                                {row?.description ?? "-"}
                              </TableCell>
                              <TableCell
                                sx={{ ...styles.td, ...styles.countCol }}
                                align="right"
                              >
                                {row?.count ?? "-"}
                              </TableCell>
                              <TableCell
                                sx={{ ...styles.td, ...styles.expandCol }}
                                align="right"
                              >
                                {hasQuery && (
                                  <IconButton
                                    onClick={() => toggleRow(key)}
                                    size="small"
                                    sx={styles.expandButton}
                                    aria-label={isExpanded ? "Hide query" : "Show query"}
                                  >
                                    {isExpanded ? (
                                      <KeyboardArrowDownIcon fontSize="small" />
                                    ) : (
                                      <KeyboardArrowUpIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                            {hasQuery && (
                              <TableRow>
                                <TableCell sx={styles.queryCell} colSpan={4}>
                                  <Collapse
                                    in={isExpanded}
                                    timeout="auto"
                                    unmountOnExit
                                  >
                                    <Box sx={styles.queryBox}>
                                      Shopify Query
                                      <Box component="code" sx={styles.queryCode}>
                                        {queryText}
                                      </Box>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                      {customerSelectionStrategySuggestions.length === 0 && (
                        <TableRow>
                          <TableCell sx={styles.td} colSpan={4}>
                            no suggestions available yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box>
                <Box component={"h3"} sx={styles.tableTitle}>
                  Product Recommendation Strategy
                </Box>
                <TableContainer component={Paper} elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...styles.th, ...styles.summaryCol }}>
                          Summary
                        </TableCell>
                        <TableCell sx={{ ...styles.th, ...styles.descriptionCol }}>
                          Description
                        </TableCell>
                        <TableCell
                          sx={{ ...styles.th, ...styles.countCol }}
                          align="right"
                        >
                          Count
                        </TableCell>
                        <TableCell sx={{ ...styles.th, ...styles.expandCol }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productRecommendationStrategySuggestions.map(
                        (row, idx) => (
                          <TableRow key={`${row?.name ?? "row"}-${idx}`}>
                            <TableCell sx={{ ...styles.td, ...styles.summaryCol }}>
                              {row?.name ?? "-"}
                            </TableCell>
                            <TableCell
                              sx={{ ...styles.td, ...styles.descriptionCol }}
                            >
                              {row?.description ?? "-"}
                            </TableCell>
                            <TableCell
                              sx={{ ...styles.td, ...styles.countCol }}
                              align="right"
                            >
                              {row?.count ?? "-"}
                            </TableCell>
                            <TableCell
                                sx={{ ...styles.td, ...styles.expandCol }}
                                align="right"
                            />
                          </TableRow>
                        )
                      )}
                      {productRecommendationStrategySuggestions.length === 0 && (
                        <TableRow>
                          <TableCell sx={styles.td} colSpan={4}>
                            no suggestions available yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

const styles = {
  root: {
    borderRadius: "10px",
    border: "1px solid #B6BEBF98",
    background: "#fff",
  },

  header: {
    alignItems: { xs: "flex-start", md: "center" },
    justifyContent: "space-between",
    px: { xs: 2, md: 4 },
    py: { xs: 2, md: 0 },
    minHeight: { xs: "auto", md: "73px" },
    zIndex: 10,
  },
  headerTitle: {
    m: 0,
    fontFamily: "FustatBold",
    fontSize: { xs: "14px", md: "19px" },
  },
  body: {
    position: "relative",
    px: { xs: 2, md: 4 },
    py: { xs: 2, md: 4 },
  },
  message: {
    color: "#696969",
    fontSize: "14px",
  },
  tableTitle: {
    m: 0,
    mb: 1,
    fontFamily: "FustatBold",
    fontSize: { xs: "13px", md: "15px" },
  },
  th: {
    fontFamily: "FustatSemiBold",
    fontSize: "15px",
    borderBottom: "1px solid #B6BEBF98",
    verticalAlign: "top",
  },
  td: {
    fontFamily: "FustatMedium",
    fontSize: "14px",
    borderBottom: "1px solid #B6BEBF30",
    verticalAlign: "top",
    wordBreak: "break-word",
  },
  summaryCol: {
    width: 220,
  },
  descriptionCol: {
    width: 420,
  },
  countCol: {
    width: 80,
  },
  expandCol: {
    width: 60,
  },
  queryToggle: {
    marginTop: "4px",
    padding: 0,
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#003300",
    fontFamily: "FustatMedium",
    fontSize: "12px",
    textDecoration: "underline",
  },
  queryCell: {
    paddingTop: 0,
    paddingBottom: 0,
    borderBottom: "none",
  },
  queryBox: {
    marginTop: 0,
    marginBottom: "8px",
    padding: "8px 12px",
    backgroundColor: "#F7F7F2",
    borderRadius: "6px",
    border: "1px solid #D0D0C8",
    fontFamily: "FustatMedium",
    fontSize: "14px",
    textTransform: "uppercase",
  },
  queryCode: {
    display: "block",
    fontFamily: "monospace",
    fontSize: "13px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    textTransform: "none",
  },
  expandButton: {
    padding: 0,
  },
};

